import { deg, fixed, trim } from '../../math/format';
import { boltChord, boltHoles, holeSpacing } from '../../math/geometry';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

export const BOLT_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'bolt.tapHole',
    topicId: 'bolt',
    skill: 'Finding a hole by its angle',
    gen: (rng) => {
      const holes = rng.pick([4, 5, 6, 8]);
      const index = rng.int(1, holes - 1);
      const spacing = 360 / holes;
      const angle = index * spacing;

      return {
        kind: 'bolt',
        holes,
        startAngle: 0,
        answerIndex: index,
        prompt: `${holes} holes, evenly spaced. Tap the hole at ${deg(angle, 0)}.`,
        why: `360 ÷ ${holes} = ${deg(spacing, 0)} per hole, so ${deg(angle, 0)} is hole ${index + 1} counting counterclockwise from 3 o’clock.`,
        steps: [
          { kind: 'formula', text: 'angle between holes = 360 ÷ number of holes' },
          { kind: 'compute', text: `360 ÷ ${holes} = ${deg(spacing, 0)}` },
          { kind: 'compute', text: `${deg(angle, 0)} ÷ ${deg(spacing, 0)} = ${index} holes past the first` },
          { kind: 'result', text: `That is hole ${index + 1}, counting the one at 3 o’clock as hole 1.` },
        ],
      };
    },
  },
  {
    id: 'bolt.spacing',
    topicId: 'bolt',
    skill: 'Degrees between holes',
    gen: (rng) => {
      const holes = rng.pick([3, 4, 5, 6, 7, 8, 9, 10, 12]);
      const solved = holeSpacing(holes);
      const correct = deg(solved.value, solved.value % 1 === 0 ? 0 : 2);

      return {
        kind: 'mc',
        prompt: `${holes} holes evenly spaced. Degrees between holes?`,
        formula: 'angle = 360 ÷ holes',
        why: `360 ÷ ${holes} = ${correct}.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: deg(180 / holes, 180 / holes % 1 === 0 ? 0 : 2),
            trap: 'That is half the spacing. 180 ÷ n shows up in the chord formula, not here.',
          },
          {
            label: deg(360 / (holes - 1), 2),
            trap: `You divided by ${holes - 1}. Every hole gets its own slice, so divide by ${holes}.`,
          },
          {
            label: deg(360 - solved.value, solved.value % 1 === 0 ? 0 : 2),
            trap: 'That is the long way round the circle, past all the other holes. You want the step to the next one.',
          },
        ]),
      };
    },
  },
  {
    id: 'bolt.radius',
    topicId: 'bolt',
    skill: 'Bolt circle diameter versus radius',
    gen: (rng) => {
      const diameter = rng.step(2, 8, 0.5);
      const radius = diameter / 2;

      return {
        kind: 'mc',
        prompt: `On a ${fixed(diameter, 3)}" bolt circle, how far is each hole from centre?`,
        formula: 'radius = diameter ÷ 2',
        why: `Bolt circles are always called out by diameter. Half of ${trim(diameter, 3)}" is ${fixed(radius, 3)}".`,
        steps: [
          { kind: 'note', text: 'A bolt circle callout is a diameter, never a radius.' },
          { kind: 'compute', text: `${trim(diameter, 3)} ÷ 2` },
          { kind: 'result', text: `= ${fixed(radius, 4)}" from centre to each hole` },
        ],
        ...mcOptions(rng, `${fixed(radius, 3)}"`, [
          {
            label: `${fixed(diameter, 3)}"`,
            trap: 'That is the diameter, straight across. Each hole sits half that from the middle.',
          },
          {
            label: `${fixed(diameter * 2, 3)}"`,
            trap: 'Doubled the diameter. The callout is already the full width of the circle.',
          },
          {
            label: `${fixed(Math.PI * diameter, 3)}"`,
            trap: 'That is the circumference, the distance all the way round the outside.',
          },
        ]),
      };
    },
  },
  {
    id: 'bolt.chord',
    topicId: 'bolt',
    skill: 'Distance between neighbouring holes',
    gen: (rng) => {
      const diameter = rng.step(2, 8, 0.5);
      const holes = rng.pick([4, 5, 6, 8]);
      const solved = boltChord(diameter, holes);
      const correct = `${fixed(solved.value, 4)}"`;

      return {
        kind: 'mc',
        prompt: `${holes} holes on a ${fixed(diameter, 3)}" bolt circle. Distance between two neighbours?`,
        formula: 'chord = D × sin(180 ÷ n)',
        why: `sin(180 ÷ ${holes}) = ${fixed(Math.sin((Math.PI * 180) / holes / 180), 4)}, and ${trim(diameter, 3)} × that = ${fixed(solved.value, 4)}".`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(solved.value / 2, 4)}"`,
            trap: 'You used the radius. The chord formula takes the full diameter.',
          },
          {
            label: `${fixed((Math.PI * diameter) / holes, 4)}"`,
            trap: 'That is the arc — the curved distance round the circle. The chord is the straight hop.',
          },
          {
            label: `${fixed(diameter / holes, 4)}"`,
            trap: 'Dividing the diameter by the hole count is not a thing. The sine is what handles the geometry.',
          },
        ]),
      };
    },
  },
  {
    id: 'bolt.coordinate',
    topicId: 'bolt',
    skill: 'X and Y for a hole',
    gen: (rng) => {
      const diameter = rng.step(2, 6, 0.5);
      const holes = rng.pick([4, 6, 8]);
      const index = rng.int(1, holes - 1);
      const solved = boltHoles(diameter, holes);
      const hole = solved.value[index]!;

      return {
        kind: 'numeric',
        prompt: `${holes} holes on a ${fixed(diameter, 3)}" bolt circle, first hole at 0°. What is the X for hole ${index + 1}?`,
        sub: 'X is measured from the centre of the circle. Four places.',
        answer: hole.x,
        tolerance: 0.0005,
        unit: '"',
        places: 4,
        formula: 'X = R × cos(angle)',
        why: `Hole ${index + 1} sits at ${deg(hole.angleDegrees, 0)}. R is ${fixed(diameter / 2, 4)}", so X = ${fixed(diameter / 2, 4)} × cos ${deg(hole.angleDegrees, 0)} = ${fixed(hole.x, 4)}".`,
        steps: [
          ...solved.steps.slice(0, 3),
          {
            kind: 'compute' as const,
            text: `Hole ${index + 1} is at ${deg(hole.angleDegrees, 0)}`,
          },
          {
            kind: 'result' as const,
            text: `X = ${fixed(diameter / 2, 4)} × cos ${deg(hole.angleDegrees, 0)} = ${fixed(hole.x, 4)}"`,
          },
          {
            kind: 'note' as const,
            text: 'A negative X just means the hole is left of centre. That is normal, not an error.',
          },
        ],
      };
    },
  },
];
